import React from 'react';
import { string, bool, oneOf } from 'prop-types';
import { pricesShape } from 'shapes';
import classnames from 'classnames';
import Price from './Price/Price';
import './Prices.scss';

export default function Prices({
    prices,
    locale,
    currency,
    isVATExcluded,
    isForeignSeller,
    showFromPrice,
    showDiscount,
    showRetailPrice,
    hideSecondaryPrice,
    size,
    mainPricePerItem,
    className,
    preserveProportions,
}) {
    const mainPrice = mainPricePerItem
        ? prices.per_item
        : prices.main_price && prices[prices.main_price];
    const secondaryPrice =
        prices.secondary_price && prices[prices.secondary_price];

    const displaySecondaryPrice = !hideSecondaryPrice && !mainPricePerItem;
    const rootClassName = classnames(
        'prices',
        {
            [`prices--${size}`]: size,
            'prices--preserve-proportions': preserveProportions,
        },
        className,
    );
    const showVATForB2B = isVATExcluded && isForeignSeller;

    return (
        <div className={rootClassName}>
            <div className="prices__main-price-wrapper">
                <Price
                    className="prices__main-price"
                    price={mainPrice}
                    isVATExcluded={isVATExcluded}
                    showVATForB2B={showVATForB2B}
                    locale={locale}
                    currency={currency}
                    showFromPrice={showFromPrice}
                    showDiscount={showDiscount}
                    showRetailPrice={showRetailPrice}
                />
            </div>

            {secondaryPrice && displaySecondaryPrice && (
                <div className="prices__secondary-wrapper">
                    <Price
                        className="prices__secondary-price"
                        price={secondaryPrice}
                        isVATExcluded={isVATExcluded}
                        showVATForB2B={showVATForB2B}
                        isPerUnit={!secondaryPrice.unit}
                        locale={locale}
                        currency={currency}
                    />
                </div>
            )}
        </div>
    );
}

Prices.propTypes = {
    /** class name  */
    className: string,
    /** Required, prices shape */
    prices: pricesShape.isRequired,
    /** locale of the platform, it's used, for example, for format prices */
    locale: string.isRequired,
    /** currency symbol */
    currency: string.isRequired,
    /** switch between VAT or no VAT price */
    isVATExcluded: bool,
    isForeignSeller: bool,
    /** display "from {price}" label. Example: "A partir de {{price}}" */
    showFromPrice: bool,
    /** display discount price */
    showDiscount: bool,
    /** display retail price, crossed out price. */
    showRetailPrice: bool,
    hideSecondaryPrice: bool,
    /** display only price per item, even if mesurement price is available, if true then the secondary price is not displayed */
    mainPricePerItem: bool,
    /** Change the size of the prices and increase the difference of size between price */
    size: oneOf(['small', 'medium', 'large']),
    /** Preserve component proportions when retail, discount, secondary price are not displayed */
    preserveProportions: bool,
};

Prices.defaultProps = {
    className: null,
    isVATExcluded: false,
    isForeignSeller: false,
    showFromPrice: false,
    showDiscount: false,
    showRetailPrice: false,
    hideSecondaryPrice: false,
    mainPricePerItem: false,
    size: 'medium',
    preserveProportions: false,
};
